import React, { useEffect, useRef } from "react";
import "@/App.css";
import "./styles/professional-theme.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TrackPackage from "./pages/TrackPackage";
import IncidentPublicView from "./pages/IncidentPublicView";
import PublicDepartures from "./pages/PublicDepartures";
import Dashboard from "./pages/Dashboard";
import UnifiedDashboard from "./pages/dashboards/UnifiedDashboard";
import ShipmentManagement from "./pages/dashboards/ShipmentManagement";
import AnalyticsDashboard from "./pages/dashboards/AnalyticsDashboard";
import PartnerShipmentsView from "./pages/dashboards/PartnerShipmentsView";
import PartnerDashboard from "./pages/dashboards/PartnerDashboard";
import AdminShipmentView from "./pages/dashboards/AdminShipmentView";
import LocalPartnerDashboard from "./pages/dashboards/LocalPartnerDashboard";
import CreateCompany from "./pages/CreateCompany";
import QRClientForm from "./pages/QRClientForm";
import LandingPageModern from "./pages/LandingPageModern";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SettingsLayout, {
  ProfileSection,
  LanguageSection,
  NotificationsSection,
  SecuritySection,
  CompanySection,
  TransportSection,
  BillingSection,
} from "./pages/SettingsPage";
import TeamPage from "./pages/TeamOrgChartSections";
import CompanyManagementPage from "./pages/CompanyInfoDynamic";
import RegisterCompany from "./pages/RegisterCompany";
import UsersManagement from "./pages/UsersManagement";
import FinanceLayout from "./pages/finance/FinanceLayout";
import FinanceShipmentsPage from "./pages/finance/FinanceShipmentsPage";
import FinanceAnnualPage from "./pages/finance/FinanceAnnualPage";
import FinanceInvoicesPage from "./pages/finance/FinanceInvoicesPage";
import FinancePricingPage from "./pages/finance/FinancePricingPage";
import FinanceAnalysisPage from "./pages/finance/FinanceAnalysisPage";
import BroadcastPage from "./pages/BroadcastPage";
import NotFound from "./pages/NotFound";
import AuditLogPage from "./pages/AuditLogPage";
import SubscriptionsAdmin from "./pages/SubscriptionsAdmin";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import CompanyActivityPage from "./pages/CompanyActivityPage";
import DeliveryPartnerActivity from "./pages/DeliveryPartnerActivity";
import CollectorActivity from "./pages/CollectorActivity";
import PlatformMonitoringPage from "./pages/PlatformMonitoringPage";
import IncidentsPage from "./pages/IncidentsPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import HelpCenter from "./pages/HelpCenter";
import TermsOfService from "./pages/TermsOfService";
import ContactPage from "./pages/ContactPage";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { usePageTracking } from "./hooks/usePageTracking";
import { useVersionCheck } from "./hooks/useVersionCheck";

function PageTracker({ children }) {
  usePageTracking();
  const needsReload = useVersionCheck();
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current !== location.pathname && needsReload.current) {
      window.location.reload();
    }
    prevPath.current = location.pathname;
  }, [location.pathname, needsReload]);

  return children;
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <WebSocketProvider>
            <BrowserRouter>
            <ErrorBoundary>
            <PageTracker>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/create-company" element={<CreateCompany />} />
              <Route path="/register-company" element={<RegisterCompany />} />
              <Route path="/qr/:qr_token" element={<QRClientForm />} />
              <Route path="/track" element={<TrackPackage />} />
              <Route path="/departures/:companyId" element={<PublicDepartures />} />
              <Route path="/incidents/view/:token" element={<IncidentPublicView />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/" element={<LandingPageModern />} />

              {/* Protected routes — require authentication */}
              <Route path="/dashboard-old" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><UnifiedDashboard /></ProtectedRoute>} />
              <Route path="/shipments" element={<ProtectedRoute deniedRoles={['platform_admin', 'loader']}><ShipmentManagement /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute deniedRoles={['loader']}><AnalyticsDashboard /></ProtectedRoute>} />
              <Route path="/my-packages" element={<ProtectedRoute deniedRoles={['platform_admin', 'loader']}><PartnerDashboard /></ProtectedRoute>} />
              <Route path="/my-packages/drafts" element={<ProtectedRoute deniedRoles={['platform_admin', 'loader']}><PartnerDashboard /></ProtectedRoute>} />
              <Route path="/my-shipments" element={<ProtectedRoute deniedRoles={['platform_admin', 'loader']}><PartnerShipmentsView /></ProtectedRoute>} />
              <Route path="/admin-shipments" element={<ProtectedRoute deniedRoles={['platform_admin', 'loader']}><AdminShipmentView /></ProtectedRoute>} />
              <Route path="/local-deliveries" element={<ProtectedRoute deniedRoles={['platform_admin', 'loader']}><LocalPartnerDashboard /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="profile" replace />} />
                <Route path="profile" element={<ProfileSection />} />
                <Route path="language" element={<LanguageSection />} />
                <Route path="notifications" element={<NotificationsSection />} />
                <Route path="security" element={<SecuritySection />} />
                <Route path="company" element={<CompanySection />} />
                <Route path="transport" element={<TransportSection />} />
                <Route path="billing" element={<BillingSection />} />
              </Route>
              <Route path="/team" element={<ProtectedRoute deniedRoles={['loader']}><TeamPage /></ProtectedRoute>} />
              <Route path="/company-management" element={<ProtectedRoute deniedRoles={['loader']}><CompanyManagementPage /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute deniedRoles={['loader']}><UsersManagement /></ProtectedRoute>} />
              <Route path="/finance" element={<ProtectedRoute deniedRoles={['loader']}><FinanceLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="shipments" replace />} />
                <Route path="shipments" element={<FinanceShipmentsPage />} />
                <Route path="annual" element={<FinanceAnnualPage />} />
                <Route path="invoices" element={<FinanceInvoicesPage />} />
                <Route path="pricing" element={<FinancePricingPage />} />
                <Route path="analysis" element={<FinanceAnalysisPage />} />
              </Route>
              <Route path="/broadcast" element={<ProtectedRoute deniedRoles={['loader']}><BroadcastPage /></ProtectedRoute>} />
              <Route path="/audit-logs" element={<ProtectedRoute deniedRoles={['loader']}><AuditLogPage /></ProtectedRoute>} />
              <Route path="/subscriptions" element={<ProtectedRoute deniedRoles={['loader']}><SubscriptionsAdmin /></ProtectedRoute>} />
              <Route path="/platform-admin" element={<ProtectedRoute deniedRoles={['loader']}><PlatformAdminPage /></ProtectedRoute>} />
              <Route path="/platform-admin/companies/:companyId/activity" element={<ProtectedRoute deniedRoles={['loader']}><CompanyActivityPage /></ProtectedRoute>} />
              <Route path="/admin/delivery-partners/:partnerId" element={<ProtectedRoute deniedRoles={['loader']}><DeliveryPartnerActivity /></ProtectedRoute>} />
              <Route path="/admin/collectors/:partnerId" element={<ProtectedRoute deniedRoles={['loader']}><CollectorActivity /></ProtectedRoute>} />
              <Route path="/monitoring" element={<ProtectedRoute deniedRoles={['loader']}><PlatformMonitoringPage /></ProtectedRoute>} />
              <Route path="/incidents" element={<ProtectedRoute deniedRoles={['loader']}><IncidentsPage /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </PageTracker>
            </ErrorBoundary>
            </BrowserRouter>
            <Toaster />
            <SonnerToaster position="top-right" richColors />
          </WebSocketProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
